// components/workspace-timeline/WorkspaceTimelineCursorOverlay.tsx
import React, { useMemo } from 'react';
import { clamp } from './presentation';

interface WorkspaceTimelineCursorOverlayProps {
  cursorPercent: number;
  dragSnapGuidePercent: number | null;
  laneLeftColumnWidthPx: number;
  timelineGridGapPx: number;
}

const TIMELINE_CURSOR_OVERLAY_LANE_STYLE: React.CSSProperties = {
  position: 'relative',
  width: '100%',
  height: '100%',
};
const TIMELINE_CURSOR_OVERLAY_CURSOR_LINE_BASE_STYLE: React.CSSProperties = {
  position: 'absolute',
  top: '44px',
  bottom: 0,
  width: '2px',
  borderRadius: '1px',
  backgroundColor: '#ECECEC',
  transform: 'translateX(-1px)',
};
const TIMELINE_CURSOR_OVERLAY_SNAP_LINE_BASE_STYLE: React.CSSProperties = {
  position: 'absolute',
  top: '44px',
  bottom: 0,
  width: '1px',
  backgroundColor: 'rgba(236,236,236,0.5)',
  transform: 'translateX(-1px)',
};

export const WorkspaceTimelineCursorOverlay = React.memo(({
  cursorPercent,
  dragSnapGuidePercent,
  laneLeftColumnWidthPx,
  timelineGridGapPx,
}: WorkspaceTimelineCursorOverlayProps) => {
  const rootStyle = useMemo<React.CSSProperties>(() => ({
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    display: 'grid',
    gridTemplateColumns: `${laneLeftColumnWidthPx}px minmax(0,1fr)`,
    columnGap: `${timelineGridGapPx}px`,
    zIndex: 25,
  }), [laneLeftColumnWidthPx, timelineGridGapPx]);
  const cursorLineStyle = useMemo<React.CSSProperties>(() => ({
    ...TIMELINE_CURSOR_OVERLAY_CURSOR_LINE_BASE_STYLE,
    left: `${clamp(cursorPercent, 0, 100)}%`,
  }), [cursorPercent]);
  const snapLineStyle = useMemo<React.CSSProperties | null>(() => {
    if (dragSnapGuidePercent === null) return null;
    return {
      ...TIMELINE_CURSOR_OVERLAY_SNAP_LINE_BASE_STYLE,
      left: `${dragSnapGuidePercent}%`,
    };
  }, [dragSnapGuidePercent]);

  return (
    <div style={rootStyle}>
      <div />
      <div style={TIMELINE_CURSOR_OVERLAY_LANE_STYLE}>
        <div style={cursorLineStyle} />
        {snapLineStyle !== null && <div style={snapLineStyle} />}
      </div>
    </div>
  );
});

WorkspaceTimelineCursorOverlay.displayName = 'WorkspaceTimelineCursorOverlay';
